<template>
  <div class="dashboard">
    <h2>欢迎使用粮库资产管理平台</h2>
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon bg-blue">
          <el-icon><Goods /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ overview.summary?.storeTotal || 0 }}</div>
          <div class="stat-label">粮库数量</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon bg-green">
          <el-icon><OfficeBuilding /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ overview.summary?.warehouseTotal || 0 }}</div>
          <div class="stat-label">仓房数量</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon bg-orange">
          <el-icon><Document /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ overview.workOrderTotal || 0 }}</div>
          <div class="stat-label">工单数量</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon bg-red">
          <el-icon><Bell /></el-icon>
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ overview.alarmPending || 0 }}</div>
          <div class="stat-label">待处理报警</div>
        </div>
      </div>
    </div>

    <div class="chart-row">
      <div class="chart-card">
        <h3>资产分布统计</h3>
        <div ref="assetChart" class="chart-container"></div>
      </div>
      <div class="chart-card">
        <h3>工单状态统计</h3>
        <div ref="workOrderChart" class="chart-container"></div>
      </div>
    </div>

    <div class="recent-section">
      <h3>最近工单</h3>
      <el-table stripe :data="recentWorkOrders" border v-loading="loading">
        <el-table-column prop="orderNo" label="工单号" width="140" />
        <el-table-column prop="title" label="工单标题" min-width="180" show-overflow-tooltip />
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getWorkOrderStatusType(row.status)" size="small">{{ row.statusText }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="priorityText" label="优先级" width="90">
          <template #default="{ row }">
            <el-tag v-if="row.priority === 3" type="danger" size="small">紧急</el-tag>
            <el-tag v-else-if="row.priority === 2" type="warning" size="small">高</el-tag>
            <el-tag v-else type="info" size="small">普通</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="grainStoreName" label="所属粮库" width="130" show-overflow-tooltip />
        <el-table-column prop="createTime" label="创建时间" width="160" />
      </el-table>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick, onUnmounted } from 'vue'
import * as echarts from 'echarts'
import { Goods, OfficeBuilding, Document, Bell } from '@element-plus/icons-vue'
import request from '../utils/request'

const overview = reactive({ summary: {} })
const recentWorkOrders = ref([])
const loading = ref(false)
const assetChart = ref(null)
const workOrderChart = ref(null)
let assetChartInstance = null
let workOrderChartInstance = null

onMounted(async () => {
  await loadData()
  nextTick(() => initCharts())
})

const loadData = async () => {
  loading.value = true
  try {
    const res = await request.get('/dashboard/overview')
    Object.assign(overview, res.data || {})
    Object.assign(overview.summary, res.data?.summary || {})
    // 最近工单：按创建时间倒序取前 5 条（overview 返回的 workOrders 已经排序好）
    const list = res.data?.workOrders || []
    recentWorkOrders.value = list.slice(0, 5)
  } catch (error) {
    console.error('加载首页数据失败', error)
  } finally {
    loading.value = false
  }
}

const initCharts = () => {
  // 资产分布饼图 — 全部用真实数据
  const s = overview.summary || {}
  assetChartInstance = echarts.init(assetChart.value)
  assetChartInstance.setOption({
    title: { text: '资产分布', left: 'center' },
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { bottom: 0 },
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
      label: { show: false, position: 'center' },
      emphasis: { label: { show: true, fontSize: 18, fontWeight: 'bold' } },
      labelLine: { show: false },
      data: [
        { value: s.storeTotal || 0, name: '粮库', itemStyle: { color: '#409eff' } },
        { value: s.warehouseTotal || 0, name: '仓房', itemStyle: { color: '#67c23a' } },
        { value: s.monitorTotal || 0, name: '监控设备', itemStyle: { color: '#e6a23c' } },
        { value: overview.lease?.total || 0, name: '合同', itemStyle: { color: '#909399' } }
      ]
    }]
  })

  // 工单状态柱状图 — 用 overview 返回的 workOrderStatus 数组
  const statusData = overview.workOrderStatus || [0, 0, 0, 0]
  workOrderChartInstance = echarts.init(workOrderChart.value)
  workOrderChartInstance.setOption({
    title: { text: '工单状态', left: 'center' },
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: ['待处理', '处理中', '已完成'] },
    yAxis: { type: 'value' },
    series: [{
      data: statusData,
      type: 'bar',
      barWidth: '50%',
      itemStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: '#83bff6' },
          { offset: 0.5, color: '#188df0' },
          { offset: 1, color: '#188df0' }
        ]),
        borderRadius: [4, 4, 0, 0]
      }
    }]
  })

  // 响应式
  window.addEventListener('resize', handleResize)
}

const handleResize = () => {
  assetChartInstance?.resize()
  workOrderChartInstance?.resize()
}

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  assetChartInstance?.dispose()
  workOrderChartInstance?.dispose()
})

const getWorkOrderStatusType = (status) => {
  if (status == null || status === 0) return 'warning'
  if (status === 1) return 'primary'
  if (status === 2) return 'success'
  if (status === 3) return 'info'
  return 'info'
}
</script>

<style scoped>
.dashboard h2 {
  margin-bottom: 20px;
  color: #303133;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
}

.stat-icon.bg-blue { background: #ecf5ff; }
.stat-icon.bg-green { background: #f0f9eb; }
.stat-icon.bg-orange { background: #fdf6ec; }
.stat-icon.bg-red { background: #fef0f0; }

.stat-icon :deep(.el-icon) { font-size: 26px; }
.stat-icon.bg-blue :deep(.el-icon) { color: #409eff; }
.stat-icon.bg-green :deep(.el-icon) { color: #67c23a; }
.stat-icon.bg-orange :deep(.el-icon) { color: #e6a23c; }
.stat-icon.bg-red :deep(.el-icon) { color: #f56c6c; }

.stat-value { font-size: 26px; font-weight: bold; color: #303133; }
.stat-label { font-size: 14px; color: #909399; }

.chart-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.chart-card {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.chart-card h3 {
  margin-bottom: 15px;
  font-size: 15px;
  color: #303133;
}

.chart-container { height: 300px; }

.recent-section {
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.recent-section h3 {
  margin-bottom: 15px;
  font-size: 15px;
  color: #303133;
}
</style>
